import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FileImageOutlined from '@ant-design/icons/FileImageOutlined';

import axiosServices from 'utils/axios';

// Renders a backend-served photo (injury report / data-collection image, etc.)
// as an <img>.
//
// A persisted photo stores a *relative* API path in `url`
// (e.g. `/api/v1/data-collection/files/...`). Using that path directly as an
// `<img src>` resolves it against the PAGE origin, which fails whenever the API
// is not exactly same-origin: in local dev (page :3000 vs API :3010) and in any
// cross-origin production build (VITE_APP_API_URL points at a separate host).
// That is why the thumbnail rendered instantly while editing (it used a local
// blob `preview`) but came back as a broken "?" box after the record was saved
// and reopened.
//
// So we fetch the file through the app's axios instance — which applies the
// correct baseURL and auth — and render the resulting object URL. Absolute /
// data / blob URLs and an offline `base64` fallback are used as-is. This mirrors
// the `urlToDataUrl` helper already used by IEPDocumentsTab.
export default function ServerImage({ photo, alt, sx, onClick, fallbackSx }) {
  // Some legacy records store the photo as a bare string (a url or data URL)
  // rather than an object. Normalise to the object shape.
  const p = typeof photo === 'string' ? { url: photo } : photo || {};

  // A data/blob URL we can show immediately without a network round-trip.
  const directSrc = p.base64 || (p.preview && /^(data:|blob:)/i.test(p.preview) ? p.preview : null);

  const [src, setSrc] = useState(directSrc);
  const [status, setStatus] = useState(directSrc ? 'ready' : 'loading');

  useEffect(() => {
    if (directSrc) {
      setSrc(directSrc);
      setStatus('ready');
      return undefined;
    }

    const url = p.url;
    if (!url) {
      setStatus('error');
      return undefined;
    }

    // Already absolute (data:, blob:, http(s):) → use verbatim.
    if (/^(data:|blob:|https?:)/i.test(url)) {
      setSrc(url);
      setStatus('ready');
      return undefined;
    }

    let cancelled = false;
    let objectUrl;
    setStatus('loading');
    (async () => {
      try {
        const res = await axiosServices.get(url, { responseType: 'blob' });
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setSrc(objectUrl);
          setStatus('ready');
        }
      } catch (err) {
        console.warn('[ServerImage] failed to load', url, err?.response?.status || err?.message);
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [p.url, directSrc]);

  if (status !== 'ready' || !src) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          ...sx,
          ...fallbackSx
        }}
        title={status === 'error' ? 'Image unavailable' : undefined}
      >
        {status === 'loading' ? <CircularProgress size={18} /> : <FileImageOutlined style={{ fontSize: 20 }} />}
      </Box>
    );
  }

  return <Box component="img" src={src} alt={alt} onClick={onClick ? () => onClick(src) : undefined} sx={sx} />;
}

ServerImage.propTypes = {
  photo: PropTypes.shape({
    url: PropTypes.string,
    base64: PropTypes.string,
    preview: PropTypes.string,
    name: PropTypes.string
  }),
  alt: PropTypes.string,
  sx: PropTypes.object,
  fallbackSx: PropTypes.object,
  // Called with the *resolved* src (object/data URL) so callers can e.g. open it
  // in a new tab — never pass the raw relative `photo.url`, it won't load.
  onClick: PropTypes.func
};
